/**
 * ADR-118 — active-organization provider.
 *
 * Owns the `activeOrganization` slice of state, persists it via
 * auth-core's storage adapter (under `StorageKeys.activeOrganization`),
 * and subscribes to auth-core's cross-tab event channel so a change
 * in tab A propagates to tab B.
 *
 * Distinct from the auth state: switching org doesn't touch token,
 * user, or any auth-identity slice. Apps can read org separately
 * from auth.
 */

import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  StorageKeys,
  bumpHeartbeat,
  createLocalStorageAdapter,
  subscribeCrossTab,
  type StorageAdapter,
} from '@aegix/auth-core';
import type { Organization, OrganizationProviderProps } from './types';

export interface OrganizationContextValue {
  activeOrganization: Organization | null;
  setActiveOrganization: (org: Organization | null) => void;
}

export const OrganizationContext =
  createContext<OrganizationContextValue | null>(null);

export function OrganizationProvider({
  storage,
  children,
}: OrganizationProviderProps) {
  // Stable storage adapter — created once, reused for the lifetime of
  // the provider. `storage` prop allows tests to inject a memory adapter.
  const storageRef = useRef<StorageAdapter>(
    storage ?? createLocalStorageAdapter(),
  );

  // Hydrate from storage on first render. The lazy initializer
  // avoids a render-after-mount flash for users who had a previous
  // active org persisted.
  const [activeOrganization, setActiveOrganizationState] =
    useState<Organization | null>(() => {
      return storageRef.current.get<Organization>(
        StorageKeys.activeOrganization,
      );
    });

  const setActiveOrganization = useCallback(
    (org: Organization | null) => {
      if (org === null) {
        storageRef.current.remove(StorageKeys.activeOrganization);
      } else {
        storageRef.current.set(StorageKeys.activeOrganization, org);
      }
      setActiveOrganizationState(org);
      // Wake other tabs. They re-read storage in the
      // subscribeCrossTab handler below.
      bumpHeartbeat();
    },
    [],
  );

  // Cross-tab sync. Other tabs writing to
  // StorageKeys.activeOrganization fire a `storage` event here; we
  // re-read storage and update local state.
  useEffect(() => {
    return subscribeCrossTab((event) => {
      if (event.key !== StorageKeys.activeOrganization) {
        // Logout / login events flow through auth-identity; the
        // active org is cleared as part of auth-core's `clearAuthKeys()`
        // sweep on logout, which will fire its own storage event for
        // this key — we'll see it on the next iteration.
        return;
      }
      const next = storageRef.current.get<Organization>(
        StorageKeys.activeOrganization,
      );
      setActiveOrganizationState(next);
    });
  }, []);

  const value = useMemo<OrganizationContextValue>(
    () => ({ activeOrganization, setActiveOrganization }),
    [activeOrganization, setActiveOrganization],
  );

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
}
